import { useEffect, useState, useCallback } from "react";
import { Outlet, useNavigate, useLocation, useParams } from "react-router-dom";
import { Menu, X, GraduationCap, School, HeartHandshake, ChevronDown, MessageCircle, FileSignature, BusFront, CalendarCheck, Landmark, CalendarDays } from "lucide-react";
import parentApi from "@/lib/parentApi";
import { usePushNotifications } from "@/lib/usePushNotifications";
import { toast } from "sonner";
import { ParentUserMenu } from "@/components/parent/ParentUserMenu";
import { ParentProfileDialog } from "@/components/parent/ParentProfileDialog";

const STUDENT_NAV = [
  { key: "messages", label: "Messages", icon: MessageCircle, badge: (s) => s.unread },
  { key: "forms", label: "Forms", icon: FileSignature, badge: (s) => s.pending_forms },
  { key: "bus", label: "Bus", icon: BusFront, when: (s) => s.is_bus_rider },
  { key: "attendance", label: "Attendance", icon: CalendarCheck },
  { key: "grades", label: "Grades", icon: GraduationCap, when: (s, sc) => sc?.company_type !== "daycare" },
];

function StudentSubNav({ school, student, active }) {
  const nav = useNavigate();
  const loc = useLocation();
  const tab = new URLSearchParams(loc.search).get("tab") || "messages";
  const onStudent = loc.pathname === `/parent/student/${student.id}`;
  const item = (key, label, Icon, badge, onClick, isActive, testid) => (
    <button key={key} onClick={onClick} data-testid={testid} className={`w-full flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] transition ${isActive ? "bg-white text-primary font-semibold shadow-sm" : "text-slate-600 hover:bg-white/70 hover:text-slate-900"}`}>
      <Icon className="h-3.5 w-3.5 shrink-0" /><span className="flex-1 truncate">{label}</span>
      {badge > 0 && <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{badge}</span>}
    </button>
  );
  if (!active) return null;
  return (
    <div className="ml-6 mt-0.5 mb-1 space-y-0.5 rounded-lg bg-slate-50 p-1" data-testid={`parent-subnav-${student.id}`}>
      {STUDENT_NAV.filter((n) => !n.when || n.when(student, school)).map((n) =>
        item(n.key, n.label, n.icon, n.badge ? n.badge(student) : 0, () => nav(`/parent/student/${student.id}?tab=${n.key}`), onStudent && tab === n.key, `parent-subnav-${n.key}-${student.id}`))}
      {item("office", "Contact office", Landmark, school.office_unread, () => nav(`/parent/office/${school.company_id}?student=${student.id}`), loc.pathname === `/parent/office/${school.company_id}`, `parent-subnav-office-${student.id}`)}
      {item("calendar", "School calendar", CalendarDays, 0, () => nav(`/parent/calendar?school=${school.company_id}&student=${student.id}`), loc.pathname === "/parent/calendar", `parent-subnav-calendar-${student.id}`)}
    </div>
  );
}

function SchoolGroup({ school, activeId, onPick, onPickClient }) {
  const hasActive = school.students.some((s) => s.id === activeId) || (school.clients || []).some((c) => c.id === activeId);
  const [open, setOpen] = useState(true);
  useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);
  const badge = school.students.reduce((a, s) => a + (s.unread || 0) + (s.pending_forms || 0), 0);
  return (
    <div data-testid={`parent-nav-school-${school.company_id}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`parent-nav-school-toggle-${school.company_id}`} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-100 text-left">
        {school.logo_data_url ? (
          <img src={school.logo_data_url} alt="" className="h-7 w-7 rounded-md object-contain bg-white border border-slate-200" />
        ) : (
          <span className="h-7 w-7 rounded-md bg-primary/10 text-primary inline-flex items-center justify-center"><School className="h-4 w-4" /></span>
        )}
        <span className="flex-1 text-[11px] font-semibold uppercase tracking-widest text-slate-500 truncate">{school.name}</span>
        {!open && badge > 0 && <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">{badge}</span>}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
      <div className="space-y-0.5 ml-2 border-l border-slate-200 pl-2">
        {school.students.map((s) => {
          const badge = (s.unread || 0) + (s.pending_forms || 0);
          const active = activeId === s.id;
          return (
            <div key={s.id}>
              <button onClick={() => onPick(s.id)} data-testid={`parent-nav-student-${s.id}`}
                className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition ${active ? "bg-primary/10 text-primary font-semibold" : "text-slate-700 hover:bg-slate-100"}`}>
                <GraduationCap className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{s.full_name}</span>
                {badge > 0 && <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white" data-testid={`parent-nav-badge-${s.id}`}>{badge}</span>}
                <ChevronDown className={`h-3.5 w-3.5 text-slate-300 transition-transform ${active ? "" : "-rotate-90"}`} />
              </button>
              <StudentSubNav school={school} student={s} active={active} />
            </div>
          );
        })}
        {(school.clients || []).map((c) => (
          <button key={c.id} onClick={() => onPickClient(c.id)} data-testid={`parent-nav-client-${c.id}`}
            className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition ${activeId === c.id ? "bg-primary/10 text-primary font-semibold" : "text-slate-700 hover:bg-slate-100"}`}>
            <HeartHandshake className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">{c.full_name}</span>
            <span className={`h-2 w-2 rounded-full ${c.on_site ? "bg-emerald-500" : "bg-slate-300"}`} title={c.on_site ? "On site now" : "Not on site"} data-testid={`parent-nav-client-dot-${c.id}`} />
          </button>
        ))}
      </div>
      )}
    </div>
  );
}

export default function ParentLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const { studentId, clientId } = useParams();
  const [me, setMe] = useState(null);
  const [schools, setSchools] = useState(null);
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { status: pushStatus, enable: enablePush } = usePushNotifications();

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([parentApi.get("/parent/me"), parentApi.get("/parent/schools")]);
      setMe(m.data); setSchools(s.data);
    } catch {
      localStorage.removeItem("vlog_parent_token");
      nav("/parent/login");
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("vlog_parent_token")) { nav("/parent/login"); return; }
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load, nav]);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const activeId = studentId || clientId || new URLSearchParams(loc.search).get("student") || new URLSearchParams(loc.search).get("client") || "";
  const pick = (id) => {
    const sc = (schools || []).find((s) => (s.students || []).some((st) => st.id === id));
    nav(`/parent/student/${id}?tab=${sc?.company_type === "daycare" ? "today" : "messages"}`);
  };
  const logout = () => { localStorage.removeItem("vlog_parent_token"); nav("/parent/login"); };
  const onEnablePush = async () => { const ok = await enablePush(); ok ? toast.success("Notifications on") : toast.error("Couldn't turn on notifications"); };
  const roleLabel = schools?.some((s) => s.kind === "care") && !schools?.some((s) => s.students?.length) ? "Family" : "Parent";
  const userMenu = <ParentUserMenu me={me} roleLabel={roleLabel} onLogout={logout} pushStatus={pushStatus} onEnablePush={onEnablePush} onEditProfile={() => setProfileOpen(true)} />;
  const profileDialog = <ParentProfileDialog open={profileOpen} me={me} onClose={() => setProfileOpen(false)} onSaved={load} />;

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-200">
        <img src="/logerd-logo.png" alt="Logerd" className="h-10 w-10 object-contain" data-testid="parent-logo" />
        <div className="min-w-0">
          <div className="font-display text-lg font-bold leading-tight tracking-tight text-slate-900">Logerd</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500" data-testid="parent-home-name">{roleLabel} portal</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4" data-testid="parent-nav">
        {schools === null && <div className="px-3 text-xs text-slate-400">Loading…</div>}
        {schools?.length === 0 && <div className="px-3 text-xs text-slate-500">No one linked yet — ask your school or care facility for an invite.</div>}
        {schools?.map((sc) => <SchoolGroup key={sc.company_id} school={sc} activeId={activeId} onPick={pick} onPickClient={(id) => nav(`/parent/client/${id}`)} />)}
      </nav>
    </div>
  );

  return (
    <div className="grid h-screen grid-cols-1 md:grid-cols-[260px_1fr] bg-slate-50" data-testid="parent-layout">
      {open && <div className="fixed inset-0 z-40 bg-slate-900/40 md:hidden" data-testid="parent-mobile-overlay" onClick={() => setOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200 transition-transform md:static md:z-auto md:translate-x-0 md:w-[260px] ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="parent-sidebar">
        <button onClick={() => setOpen(false)} className="md:hidden absolute right-3 top-4 h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 inline-flex items-center justify-center" data-testid="parent-mobile-close"><X className="h-4 w-4" /></button>
        {sidebar}
      </aside>
      <main className="flex flex-col min-h-0 overflow-y-auto">
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 bg-white border-b border-slate-200 px-4 py-3">
          <button onClick={() => setOpen(true)} data-testid="parent-mobile-menu-btn" className="h-9 w-9 rounded-lg border border-slate-200 inline-flex items-center justify-center text-slate-700"><Menu className="h-5 w-5" /></button>
          <span className="flex items-center gap-2 font-display font-bold text-slate-900 flex-1"><img src="/logerd-logo.png" alt="" className="h-7 w-7 object-contain" /> Logerd</span>
          {userMenu}
        </div>
        <div className="hidden md:flex justify-end px-6 pt-3" data-testid="parent-topbar">{userMenu}</div>
        <Outlet context={{ me, schools, reload: load }} />
      </main>
      {profileDialog}
    </div>
  );
}
